import { useMemo, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { formatUsd, rollupBomForSubtree } from '@/features/projects/rollup-bom'
import type { ProjectCatalog, StepNode } from '@/api/types'

interface MilestoneBomStripProps {
  step: StepNode
  catalog: ProjectCatalog
}

const formatQty = (qty: number): string => {
  if (Number.isInteger(qty)) return String(qty)
  return qty.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * Inline rollup that lives directly under a milestone Step in the editor + viewer. Shows the
 * subtree's totals collapsed by default; expands to a per-line breakdown on click.
 */
export const MilestoneBomStrip = ({ step, catalog }: MilestoneBomStripProps) => {
  const [expanded, setExpanded] = useState(false)
  const rollup = useMemo(() => rollupBomForSubtree(step, catalog), [step, catalog])
  const isEmpty = rollup.materials.length === 0 && rollup.tools.length === 0

  return (
    <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-sm">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={expanded}
      >
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Milestone BOM
        </span>
        <span className="flex items-center gap-2">
          <span className="font-medium tabular-nums">
            {isEmpty ? 'Nothing yet' : formatUsd(rollup.grandTotalCents)}
          </span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </span>
      </button>

      {expanded && !isEmpty && (
        <div className="mt-2 space-y-3 border-t pt-2">
          {rollup.materials.length > 0 && (
            <ul className="space-y-1">
              {rollup.materials.map((line) => (
                <li
                  key={line.catalogMaterialId}
                  className="flex items-baseline justify-between gap-3 text-xs"
                >
                  <span className="flex-1 truncate">{line.name}</span>
                  <span className="text-muted-foreground">
                    {formatQty(line.totalQty)} {line.unit} × {formatUsd(line.unitPriceCents)}
                  </span>
                  <span className="w-16 text-right tabular-nums">
                    {formatUsd(line.lineTotalCents)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {rollup.tools.length > 0 && (
            <ul className="space-y-1">
              {rollup.tools.map((line) => (
                <li
                  key={line.catalogToolId}
                  className="flex items-baseline justify-between gap-3 text-xs"
                >
                  <span className="flex-1 truncate">{line.name}</span>
                  <span className="w-16 text-right tabular-nums">
                    {formatUsd(line.unitPriceCents)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {expanded && isEmpty && (
        <p className="mt-2 text-xs text-muted-foreground">
          No materials or tools attached anywhere under this Milestone.
        </p>
      )}
    </div>
  )
}
